/**
 * Quick Actions Widget Component
 * Sprint 13 - Dashboard enhancement
 * 
 * Features:
 * - Risk Matrix summary with high/critical count
 * - One-click exports (PNG/PDF)
 * - Financial variance snapshot
 * - Webhook health indicator
 */

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  AlertTriangle, TrendingUp, TrendingDown, Download, 
  Shield, Activity, DollarSign, FileImage, FileText,
  ChevronRight, Loader2, CheckCircle, XCircle, AlertCircle
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API_BASE = process.env.REACT_APP_BACKEND_URL + '/api';
const getAuthHeaders = () => {
  const token = localStorage.getItem('simpliview_token');
  const tenantId = localStorage.getItem('impersonated_tenant_id') || localStorage.getItem('simpliview_user_tenant_id');
  const headers = {};
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }
  if (tenantId) {
    headers['X-Tenant-ID'] = tenantId;
  }
  return headers;
};

const formatCurrency = (amount) => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 0
  }).format(amount || 0);
};

const QuickActionsWidget = ({ className = '' }) => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState({
    risks: { high: 0, critical: 0, total: 0 },
    variance: { percentage: null, amount: null },
    webhookHealth: 'unknown'
  });
  const [exporting, setExporting] = useState(null);

  useEffect(() => {
    const loadData = async () => {
      try {
        // Load risk matrix summary
        const riskRes = await axios.get(`${API_BASE}/risks/matrix`, { 
          headers: getAuthHeaders(),
          params: { status: 'open' }
        });
        const riskSummary = riskRes.data.summary || {};
        
        // Load financial variance (latest)
        let varianceData = { percentage: null, amount: null };
        try {
          const varianceRes = await axios.get(`${API_BASE}/financial/variances`, { 
            headers: getAuthHeaders(),
            params: { limit: 1 }
          });
          const variances = varianceRes.data.variances || [];
          if (variances.length > 0) {
            varianceData = {
              percentage: variances[0].variance_percentage,
              amount: variances[0].delta_cost
            };
          }
        } catch (e) {
          console.log('No variance data available');
        }
        
        // Load webhook health
        let webhookHealth = 'unknown';
        try {
          const webhookRes = await axios.get(`${API_BASE}/webhooks/health/summary`, { 
            headers: getAuthHeaders()
          });
          const health = webhookRes.data;
          if (health.error_rate > 10) {
            webhookHealth = 'red';
          } else if (health.error_rate > 2) {
            webhookHealth = 'amber';
          } else {
            webhookHealth = 'green';
          }
        } catch (e) {
          webhookHealth = 'green'; // Default to green if no data
        }
        
        setData({
          risks: {
            high: riskSummary.high || 0,
            critical: riskSummary.critical || 0,
            total: riskSummary.total || 0
          },
          variance: varianceData,
          webhookHealth
        });
      } catch (err) {
        console.error('Failed to load quick actions data:', err);
      } finally {
        setLoading(false);
      }
    };
    
    loadData();
  }, []);

  const handleExport = async (format) => {
    setExporting(format);
    try {
      const endpoint = format === 'png' 
        ? `${API_BASE}/risks/export/png`
        : `${API_BASE}/risks/export/pdf`;
      
      const response = await axios.get(endpoint, {
        headers: getAuthHeaders(),
        responseType: 'blob'
      });
      
      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `risk_matrix.${format}`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success(`Risk matrix exported as ${format.toUpperCase()}`);
    } catch (err) {
      console.error('Export failed:', err);
      toast.error(`Failed to export ${format.toUpperCase()}`);
    } finally {
      setExporting(null);
    }
  };

  const getHealthColor = (status) => {
    switch (status) {
      case 'green': return 'text-green-500 bg-green-100';
      case 'amber': return 'text-amber-500 bg-amber-100';
      case 'red': return 'text-red-500 bg-red-100';
      default: return 'text-gray-400 bg-gray-100';
    }
  };

  const getHealthIcon = (status) => {
    switch (status) {
      case 'green': return <CheckCircle className="w-4 h-4" />;
      case 'amber': return <AlertCircle className="w-4 h-4" />;
      case 'red': return <XCircle className="w-4 h-4" />;
      default: return <Activity className="w-4 h-4" />;
    }
  };

  if (loading) {
    return (
      <div className={`bg-white border rounded-xl p-4 ${className}`}>
        <div className="flex items-center justify-center py-8">
          <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
        </div>
      </div>
    );
  }

  return (
    <div className={`bg-white border rounded-xl shadow-sm overflow-hidden ${className}`} data-testid="quick-actions-widget">
      <div className="px-4 py-3 bg-gradient-to-r from-slate-800 to-slate-700 text-white">
        <h3 className="font-semibold text-sm flex items-center gap-2">
          <Activity className="w-4 h-4" />
          Quick Actions
        </h3>
      </div>
      
      <div className="p-4 space-y-4">
        {/* Risk Matrix Summary */}
        <div 
          className="flex items-center justify-between p-3 bg-gray-50 rounded-lg hover:bg-gray-100 cursor-pointer transition-colors"
          onClick={() => navigate('/analytics/risk-matrix')}
          data-testid="quick-action-risks"
        >
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-red-100 flex items-center justify-center">
              <Shield className="w-5 h-5 text-red-600" />
            </div>
            <div>
              <div className="text-sm font-medium text-gray-900">Risk Matrix</div>
              <div className="text-xs text-gray-500">
                {data.risks.critical > 0 && (
                  <span className="text-red-600 font-semibold">{data.risks.critical} critical</span>
                )}
                {data.risks.critical > 0 && data.risks.high > 0 && <span className="mx-1">•</span>}
                {data.risks.high > 0 && (
                  <span className="text-orange-600 font-semibold">{data.risks.high} high</span>
                )}
                {data.risks.critical === 0 && data.risks.high === 0 && (
                  <span className="text-green-600">All clear</span>
                )}
              </div>
            </div>
          </div>
          <ChevronRight className="w-4 h-4 text-gray-400" />
        </div>
        
        {/* Export Buttons */}
        <div className="flex gap-2">
          <button
            onClick={() => handleExport('png')}
            disabled={exporting !== null}
            className="flex-1 flex items-center justify-center gap-2 px-3 py-2 bg-blue-50 text-blue-700 rounded-lg text-sm font-medium hover:bg-blue-100 transition-colors disabled:opacity-50"
            data-testid="export-png-btn"
          >
            {exporting === 'png' ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <FileImage className="w-4 h-4" />
            )}
            PNG
          </button>
          <button
            onClick={() => handleExport('pdf')}
            disabled={exporting !== null}
            className="flex-1 flex items-center justify-center gap-2 px-3 py-2 bg-purple-50 text-purple-700 rounded-lg text-sm font-medium hover:bg-purple-100 transition-colors disabled:opacity-50"
            data-testid="export-pdf-btn"
          >
            {exporting === 'pdf' ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <FileText className="w-4 h-4" />
            )}
            PDF
          </button>
        </div>
        
        {/* Financial Variance */}
        <div 
          className="flex items-center justify-between p-3 bg-gray-50 rounded-lg hover:bg-gray-100 cursor-pointer transition-colors"
          onClick={() => navigate('/financial?tab=variance')}
          data-testid="quick-action-variance"
        >
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-green-100 flex items-center justify-center">
              <DollarSign className="w-5 h-5 text-green-600" />
            </div>
            <div>
              <div className="text-sm font-medium text-gray-900">Financial Variance</div>
              {data.variance.percentage !== null ? (
                <div className={`text-xs font-semibold ${
                  data.variance.percentage >= 0 ? 'text-red-600' : 'text-green-600'
                }`}>
                  {data.variance.percentage >= 0 ? '+' : ''}{data.variance.percentage?.toFixed(1)}%
                  <span className="text-gray-500 font-normal ml-1">
                    ({formatCurrency(data.variance.amount)})
                  </span>
                </div>
              ) : (
                <div className="text-xs text-gray-500">No variances recorded</div>
              )}
            </div>
          </div>
          {data.variance.percentage !== null && (
            data.variance.percentage >= 0 
              ? <TrendingUp className="w-4 h-4 text-red-500" />
              : <TrendingDown className="w-4 h-4 text-green-500" />
          )}
        </div>
        
        {/* Webhook Health */}
        <div 
          className="flex items-center justify-between p-3 bg-gray-50 rounded-lg hover:bg-gray-100 cursor-pointer transition-colors"
          onClick={() => navigate('/settings/webhooks')}
          data-testid="quick-action-webhooks"
        >
          <div className="flex items-center gap-3">
            <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${getHealthColor(data.webhookHealth)}`}>
              {getHealthIcon(data.webhookHealth)}
            </div>
            <div>
              <div className="text-sm font-medium text-gray-900">Webhook Health</div>
              <div className="text-xs text-gray-500">
                {data.webhookHealth === 'green' && 'All integrations healthy'}
                {data.webhookHealth === 'amber' && 'Minor issues detected'}
                {data.webhookHealth === 'red' && 'Attention required'}
                {data.webhookHealth === 'unknown' && 'Status unknown'}
              </div>
            </div>
          </div>
          <ChevronRight className="w-4 h-4 text-gray-400" />
        </div>
      </div>
    </div>
  );
};

export default QuickActionsWidget;
